/* eslint-disable @typescript-eslint/no-unused-vars */
import * as React from 'react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import loginImg from '../../src/VESTN_FullLogo.png';
import { API_ENDPOINTS } from '../config/api';
import { services } from '../services'; // your existing axios wrapper
import { APIError, LoginPageProps } from '../types/login/login';
import { Button } from './ui/button';
import { Input } from './ui/input';

const LoginPage: React.FC<LoginPageProps> = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await services.post(API_ENDPOINTS.AUTH.LOGIN, {
        email: email,
        password: password,
      });

      if (typeof response === 'string' || response instanceof Error) {
        throw new Error('Authentication failed');
      }

      if (response.status < 200 || response.status >= 300) {
        throw new Error(response.statusText);
      }

      const { token, user } = response.data.data;

      // Check if user role is admin
      if (user.role !== 'admin') {
        setError('Access denied. Only admin users can access this panel.');
        setLoading(false);
        return;
      }

      // Save token and user details
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));

      console.log('Login successful, token saved:', token);
      console.log('User data saved:', user);

      // Call onLogin to update parent state
      onLogin();

      // Also navigate directly as backup
      navigate('/dashboard');
    } catch (err) {
      const error = err as APIError;
      setError(error.data?.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>
        {`
          input:-webkit-autofill,
          input:-webkit-autofill:hover,
          input:-webkit-autofill:focus,
          input:-webkit-autofill:active {
            -webkit-box-shadow: 0 0 0 30px #1a2f3a inset !important;
            -webkit-text-fill-color: #ede8c5 !important;
            background-color: #1a2f3a !important;
          }
          
          input[type="email"]:-webkit-autofill,
          input[type="password"]:-webkit-autofill {
            -webkit-box-shadow: 0 0 0 30px #1a2f3a inset !important;
            -webkit-text-fill-color: #ede8c5 !important;
            background-color: #1a2f3a !important;
          }
        `}
      </style>
      <div
        className='dark min-h-screen flex flex-col items-center justify-center text-foreground p-4'
        style={{
          backgroundColor: '#0B222C',
          backgroundImage:
            'radial-gradient(circle at 20% 80%, rgba(187, 228, 37, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(9, 93, 82, 0.15) 0%, transparent 50%)',
        }}
      >
        {/* Heading */}
        <h1
          className='text-3xl md:text-4xl font-bold mb-8 text-center font-dm-serif'
          style={{
            color: '#ede8c5',
            textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
            letterSpacing: '0.5px',
          }}
        >
          Admin Panel
        </h1>

        {/* Card */}
        <div
          className='flex w-full max-w-4xl shadow-2xl border border-border rounded-xl overflow-hidden'
          style={{
            backgroundColor: '#0B222C',
            borderColor: '#095D52',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.1)',
            backdropFilter: 'blur(10px)',
          }}
        >
          {/* Left side - Image */}
          <div
            className='hidden md:block md:w-1/2'
            style={{
              backgroundColor: '#0B222C',
              backgroundImage:
                'linear-gradient(135deg, rgba(187, 228, 37, 0.05) 0%, rgba(9, 93, 82, 0.1) 100%)',
            }}
          >
            <div className='h-full flex items-center justify-center p-8'>
              <div className='relative'>
                <div
                  className='absolute inset-0 rounded-full blur-xl'
                  style={{
                    background:
                      'radial-gradient(circle, rgba(187, 228, 37, 0.2) 0%, transparent 70%)',
                    transform: 'scale(1.2)',
                  }}
                />
                <img
                  src={loginImg}
                  alt='Login Illustration'
                  className='object-contain max-h-80 w-auto relative z-10'
                  style={{ filter: 'drop-shadow(0 10px 20px rgba(0, 0, 0, 0.3))' }}
                />
              </div>
            </div>
          </div>

          {/* Right side - Login Form */}
          <div
            className='w-full md:w-1/2 p-8'
            style={{
              backgroundColor: '#0B222C',
              borderColor: '#095D52',
              backgroundImage:
                'linear-gradient(135deg, rgba(187, 228, 37, 0.02) 0%, rgba(9, 93, 82, 0.05) 100%)',
            }}
          >
            <div className='mb-8'>
              <h2
                className='text-2xl font-semibold text-center tracking-tight font-dm-serif'
                style={{
                  color: '#ede8c5',
                  textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
                  letterSpacing: '0.3px',
                }}
              >
                Sign in
              </h2>
              <p
                className='text-center mt-2 text-sm opacity-90 font-work-sans'
                style={{ color: '#ede8c5' }}
              >
                Please enter your credentials
              </p>
            </div>

            <form className='space-y-6' onSubmit={submit}>
              <div className='space-y-5'>
                <div className='space-y-3'>
                  <label
                    htmlFor='email'
                    className='text-sm font-medium block font-work-sans'
                    style={{
                      color: '#ede8c5',
                      textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
                    }}
                  >
                    Email
                  </label>
                  <Input
                    id='email'
                    name='email'
                    type='email'
                    autoComplete='email'
                    required
                    placeholder='Enter your email'
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className='w-full transition-all duration-200 focus:ring-2 focus:ring-opacity-50 font-work-sans'
                    style={{
                      backgroundColor: '#1a2f3a !important',
                      borderColor: '#bbe425',
                      color: '#ede8c5',
                      borderWidth: '2px',
                      borderRadius: '8px',
                      padding: '12px 16px',
                      boxShadow:
                        '0 2px 4px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(187, 228, 37, 0.1)',
                      transition: 'all 0.2s ease',
                    }}
                  />
                </div>

                <div className='space-y-3'>
                  <label
                    htmlFor='password'
                    className='text-sm font-medium block font-work-sans'
                    style={{
                      color: '#ede8c5',
                      textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
                    }}
                  >
                    Password
                  </label>
                  <Input
                    id='password'
                    name='password'
                    type='password'
                    autoComplete='current-password'
                    required
                    placeholder='Enter your password'
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className='w-full transition-all duration-200 focus:ring-2 focus:ring-opacity-50 font-work-sans'
                    style={{
                      backgroundColor: '#1a2f3a !important',
                      borderColor: '#bbe425',
                      color: '#ede8c5',
                      borderWidth: '2px',
                      borderRadius: '8px',
                      padding: '12px 16px',
                      boxShadow:
                        '0 2px 4px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(187, 228, 37, 0.1)',
                      transition: 'all 0.2s ease',
                    }}
                  />
                </div>
              </div>

              {error && (
                <div
                  className='text-sm text-center p-3 rounded-lg border font-work-sans'
                  style={{
                    color: '#ff6b6b',
                    backgroundColor: 'rgba(255, 107, 107, 0.1)',
                    borderColor: 'rgba(255, 107, 107, 0.3)',
                    boxShadow: '0 2px 4px rgba(255, 107, 107, 0.1)',
                  }}
                >
                  {error}
                </div>
              )}

              <Button
                type='submit'
                className='w-full py-3 text-base font-semibold transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98] font-work-sans'
                style={{
                  backgroundColor: '#bbe425',
                  color: '#0B222C',
                  border: 'none',
                  fontWeight: 'bold',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(187, 228, 37, 0.3), 0 2px 4px rgba(0, 0, 0, 0.1)',
                  textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
                }}
                disabled={loading}
              >
                {loading ? (
                  <div className='flex items-center justify-center'>
                    <svg
                      className='animate-spin h-5 w-5'
                      style={{ color: '#0B222C' }}
                      xmlns='http://www.w3.org/2000/svg'
                      fill='none'
                      viewBox='0 0 24 24'
                    >
                      <circle
                        className='opacity-25'
                        cx='12'
                        cy='12'
                        r='10'
                        stroke='currentColor'
                        strokeWidth='4'
                      ></circle>
                      <path
                        className='opacity-75'
                        fill='currentColor'
                        d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                      ></path>
                    </svg>
                  </div>
                ) : (
                  'Sign in'
                )}
              </Button>
              <div className='flex items-center justify-center text-sm mt-4'>
                <a
                  className='underline transition-all duration-200 hover:opacity-80 font-work-sans'
                  onClick={() => navigate('/forgot-password')}
                  style={{
                    color: '#ede8c5',
                    textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
                  }}
                >
                  Forgot password?
                </a>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default LoginPage;
